import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API_BASE, getStoredToken, setAuthToken, storeToken } from "@/lib/apiClient";
import { markPendingOnboarding, clearPendingOnboarding } from "@/lib/onboardingFlags";
import { clearFreshLogin, markFreshLogin } from "@/lib/sessionFlags";

const AuthContext = createContext({
  user: null,
  token: null,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  refreshSession: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => getStoredToken());
  const [loading, setLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    const t = getStoredToken();
    if (!t) {
      setUser(null);
      setToken(null);
      setAuthToken(null);
      setLoading(false);
      return null;
    }
    setAuthToken(t);
    try {
      const r = await axios.get(`${API_BASE}/auth/me`);
      setUser(r.data.user);
      setToken(t);
      return r.data;
    } catch {
      storeToken(null);
      setAuthToken(null);
      setUser(null);
      setToken(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const login = useCallback(async (email, password) => {
    const r = await axios.post(`${API_BASE}/auth/login`, { email, password });
    localStorage.removeItem("bizpa_onboarded");
    storeToken(r.data.token);
    setAuthToken(r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    setLoading(false);
    markFreshLogin();
    return r.data;
  }, []);

  const register = useCallback(async (email, password, owner_name) => {
    const r = await axios.post(`${API_BASE}/auth/register`, {
      email,
      password,
      owner_name,
    });
    localStorage.removeItem("bizpa_onboarded");
    markPendingOnboarding();
    storeToken(r.data.token);
    setAuthToken(r.data.token);
    setToken(r.data.token);
    setUser(r.data.user);
    setLoading(false);
    markFreshLogin();
    return r.data;
  }, []);

  const logout = useCallback(() => {
    storeToken(null);
    setAuthToken(null);
    setToken(null);
    setUser(null);
    localStorage.removeItem("bizpa_onboarded");
    clearPendingOnboarding();
    clearFreshLogin();
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, token, loading, login, register, logout, refreshSession }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
